import DataManager from '@americanexpress/data-manager';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { OneExperimentsProvider } from '@americanexpress/one-experiments';
import { ExperimentProvider } from '@americanexpress/one-xp-client-sdk';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { ApolloProvider } from '@apollo/client';
import {
  AmexDDLRegister,
  getAmexDataManagerConfig,
} from 'axp-data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { OneFetchyeProvider } from 'fetchye-one-app';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import childRoutes from '../childRoutes';
import reducer from '../duck';
import createGraphQLClient from '../utils/createGraphQLClient';
import {
  shouldDisableDefaultView,
  getCustomDDLConfig,
} from '../utils/ddlConfig';
import BankingRootWrapper from './BankingRootWrapper';
import AnalyticsErrorBoundary from './AnalyticsErrorBoundary';

const queryClient = new QueryClient();

export const BankingRoot = ({
  children,
  locale,
  graphApiUrl,
  dispatchAddErrorToReport,
}) => {
  const applicationTitle = 'American Express Credit Cards, Rewards & Banking';

  const client = useMemo(
    () => createGraphQLClient(graphApiUrl, dispatchAddErrorToReport),
    [graphApiUrl, dispatchAddErrorToReport]
  );

  const { pathname } = global.BROWSER ? window.location : {};
  const publicGuid =
    global.BROWSER &&
    document?.cookie?.split('blueboxpublic=')[1]?.split(';')[0];

  const shouldBypassOneXpConsent = useSelector((state) =>
    state.getIn(['config', 'bypassIsOneXpConsentAccepted'])
  );

  // Enables one-xp calls on local
  if (global.BROWSER && shouldBypassOneXpConsent === 'true') {
    window.isOneXpConsentAccepted = true;
  }
  const {
    ddlProviderProps: {
      initialDDL: { ddl: defaultDDL },
    },
  } = getAmexDataManagerConfig();

  // This works because we expect to <BankingRoot/> to always re-render on path change
  useEffect(() => {
    if (global.BROWSER) {
      document.documentElement.scrollTo(0, 0);
    }
  }, [pathname]);

  useEffect(() => {
    /* eslint-disable no-underscore-dangle */
    if (typeof Intl.__disableRegExpRestore === 'function') {
      Intl.__disableRegExpRestore();
    }
    /* eslint-enable no-underscore-dangle */
  }, []);

  return (
    <Fragment>
      <Helmet
        htmlAttributes={{
          lang: locale,
        }}
        meta={[
          { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1' },
          { name: 'application-name', content: applicationTitle },
        ]}
        link={[
          { rel: 'icon', href: 'https://www.americanexpress.com/favicon.ico' },
        ]}
      />
      <DataManager
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getAmexDataManagerConfig({
          initialDDL: {
            historyLimit: 20,
            ...(shouldDisableDefaultView(pathname)
              ? {
                  ddl: {
                    ...defaultDDL,
                    view: getCustomDDLConfig(locale),
                  },
                }
              : {}),
          },
        })}
      >
        <SharedScript
          name="one-stream-data-handler"
          windowCheck="oneStreamData"
          version="^0.1.1"
        />
        <SharedScript
          name="one-identity-session"
          version="^1.0.0"
          windowCheck="AmexSession"
        />
        <AnalyticsErrorBoundary>
          {shouldDisableDefaultView(pathname) ? (
            <AmexDDLRegister disableRegisterList={{ view: true }} />
          ) : (
            <AmexDDLRegister />
          )}
          {publicGuid && (
            <DDLRegister name="user.publicGuid" value={publicGuid} />
          )}
          <Adobe version="^1.0.0" />
        </AnalyticsErrorBoundary>
        <ExperimentProvider>
          <OneExperimentsProvider>
            <OneFetchyeProvider>
              <QueryClientProvider client={queryClient}>
                <ApolloProvider client={client}>
                  <BankingRootWrapper>{children}</BankingRootWrapper>
                </ApolloProvider>
              </QueryClientProvider>
            </OneFetchyeProvider>
          </OneExperimentsProvider>
        </ExperimentProvider>
      </DataManager>
    </Fragment>
  );
};

BankingRoot.childRoutes = childRoutes;

/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require
  BankingRoot.appConfig = require('../appConfig').default;
}

export const mapStateToProps = (state) => ({
  locale: state.getIn(['intl', 'activeLocale'], null),
  graphApiUrl: state.getIn(['config', 'graphApiUrl'], null),
});

export const mapDispatchToProps = (dispatch) => ({
  dispatchAddErrorToReport: (error, args) =>
    dispatch(addErrorToReport(error, args)),
});

BankingRoot.propTypes = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.string,
  graphApiUrl: PropTypes.string.isRequired,
  dispatchAddErrorToReport: PropTypes.func.isRequired,
};

BankingRoot.defaultProps = {
  locale: null,
};

BankingRoot.holocron = {
  name: 'banking-root',
  reducer,
};

export default compose(
  oneAppModuleWrapper('banking-root', {}),
  connect(mapStateToProps, mapDispatchToProps)
)(BankingRoot);
